import { Vue, Component, Prop } from 'vue-property-decorator';
import { PricingTableHelper } from '@lib-shared/editor/helpers/pricing-table.helper';
import { fallbackPricingTableDescriptionContent } from '@lib-shared/editor/constants';
import {
  IBlockWidth,
  IField,
  IMergeValues,
  IPricingTableColumn,
  IPricingTableCurrencySettings,
  IPricingTableRow,
  IPricingTableRowValue,
} from '@lib-shared/editor/types';
import { EContext, ENumberInputType, EPricingTableColumn } from '@lib-shared/editor/types/enums';
import { translate } from '@lib-shared/helpers/translation.helper';
import { EditorHelper } from '@lib-shared/editor/helpers/editor.helper';

@Component
export default class PricingTableRowRoot extends Vue {
  @Prop() row: IPricingTableRow;
  @Prop() columns: IPricingTableColumn[];
  @Prop() nodeSelected: boolean;
  @Prop() rowSelected: boolean;
  @Prop() columnIdSelected: string;
  @Prop() gridTemplateColumns: string;
  @Prop() currencySettings: IPricingTableCurrencySettings;
  @Prop() isPreCalculated: boolean;
  @Prop() blockWidth: IBlockWidth;
  @Prop() mergeValues: IMergeValues;
  @Prop() isTemplate: boolean;
  @Prop() isAddingRecipient: boolean;
  @Prop() fields: IField[];
  @Prop() allEditorFields: IField[];
  @Prop() allCustomTags: IField[];
  @Prop() context: EContext;

  get rowValues(): IPricingTableRowValue[] {
    return this.row?.values;
  }

  get discountFlatFee(): boolean {
    return this.row?.discountFlatFee;
  }

  get taxFlatFee(): boolean {
    return this.row?.taxFlatFee;
  }

  get columnNameId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.Name)?.id;
  }

  get columnDescriptionId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.Description)?.id;
  }

  get columnSkuId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.Sku)?.id;
  }

  get columnPriceId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.Price)?.id;
  }

  get columnUnitsId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.Units)?.id;
  }

  get columnTaxId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.Tax)?.id;
  }

  get columnDiscountId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.Discount)?.id;
  }

  get columnNetPriceId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.NetPrice)?.id;
  }

  get columnTotalPriceId(): string {
    return this.columns.find(column => column.name === EPricingTableColumn.TotalPrice)?.id;
  }

  get name(): string {
    return this.rowValues.find(rowValue => rowValue.columnId === this.columnNameId)?.value;
  }

  get description(): string {
    const content = this.rowValues.find(rowValue => rowValue.columnId === this.columnDescriptionId)
      ?.value;
    if (!content) {
      return JSON.parse(fallbackPricingTableDescriptionContent);
    } else if (EditorHelper.IsJsonString(content)) {
      return JSON.parse(content);
    } else {
      return content;
    }
  }

  get descriptionIsEmpty(): boolean {
    return JSON.stringify(this.description) === fallbackPricingTableDescriptionContent;
  }

  get sku(): string {
    return this.rowValues.find(rowValue => rowValue.columnId === this.columnSkuId)?.value;
  }

  get skuIsEmpty(): boolean {
    return this.sku === '0' || this.sku === '';
  }

  get discount(): string {
    return this.rowValues.find(rowValue => rowValue.columnId === this.columnDiscountId)?.value;
  }

  get discountEnabled(): boolean {
    return this.columns.find(column => column.name === EPricingTableColumn.Discount)?.enabled;
  }

  get tax(): string {
    return this.rowValues.find(rowValue => rowValue.columnId === this.columnTaxId)?.value;
  }

  get netPrice(): string {
    return this.rowValues.find(rowValue => rowValue.columnId === this.columnNetPriceId)?.value;
  }

  get discountPercentagePreview(): string {
    const discountInCurrency = String((Number(this.totalPrice) * Number(this.discount)) / 100);
    return `-${PricingTableHelper.currencyFormat(discountInCurrency, this.currencySettings)} (${
      this.discount
    }%)`;
  }

  get taxPercentagePreview(): string {
    const taxInCurrency = String((Number(this.priceBeforeDiscounts) * Number(this.tax)) / 100);
    return `${PricingTableHelper.currencyFormat(taxInCurrency, this.currencySettings)} (${
      this.tax
    }%)`;
  }

  get totalPrice(): string {
    return this.rowValues.find(rowValue => rowValue.columnId === this.columnTotalPriceId)?.value;
  }

  get priceBeforeDiscounts(): string {
    const price = Number(
      this.rowValues.find(rowValue => rowValue.columnId === this.columnPriceId)?.value
    );
    const units = Number(
      this.rowValues.find(rowValue => rowValue.columnId === this.columnUnitsId)?.value
    );
    return String(price * units);
  }

  get formattedTotalPrice(): string {
    return PricingTableHelper.currencyFormat(this.totalPrice, this.currencySettings);
  }

  get discountIsEmpty(): boolean {
    const discountValue = this.discount;
    return discountValue === '0' || discountValue === '';
  }

  get formattedDiscount(): string {
    if (this.discountIsEmpty) {
      return translate('add-discount');
    } else if (this.discountFlatFee) {
      return `-${PricingTableHelper.currencyFormat(this.discount, this.currencySettings)}`;
    } else {
      return this.discountPercentagePreview;
    }
  }

  get formattedTax(): string {
    if (this.taxFlatFee) {
      return `${PricingTableHelper.currencyFormat(this.tax, this.currencySettings)}`;
    } else {
      return `${this.taxPercentagePreview}`;
    }
  }

  get discountedPrice(): string {
    return PricingTableHelper.currencyFormat(this.netPrice, this.currencySettings);
  }

  get dataColumnValues(): IPricingTableRowValue[] {
    const enabledColumnIds = this.columns.filter(column => column.enabled).map(column => column.id);
    return this.rowValues.filter(
      rowValue =>
        ![
          this.columnNameId,
          this.columnDescriptionId,
          this.columnSkuId,
          this.columnTotalPriceId,
          this.columnNetPriceId,
          this.columnDiscountId,
        ].includes(rowValue.columnId) && enabledColumnIds.includes(rowValue.columnId)
    );
  }

  get showDiscount(): boolean {
    return (
      (this.discountEnabled && !this.discountIsEmpty) || (this.rowSelected && this.discountEnabled)
    );
  }

  getNumberInputType(columnId: string): ENumberInputType {
    switch (columnId) {
      case this.columnPriceId:
        return ENumberInputType.Currency;

      case this.columnTotalPriceId:
        return ENumberInputType.Currency;

      case this.columnTaxId:
        return ENumberInputType.Percentage;

      default:
        return ENumberInputType.Number;
    }
  }

  getColumnDisplayName(columnId: string): string {
    const column = this.columns.find(({ id }) => id === columnId);
    if (!column) {
      return '';
    }
    return column.displayName || column.name;
  }
}
